import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import ButtonComp from 'common/components/Button/ButtonComp';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { AI_ACTIONS } from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { GENERIC_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationCommonSaveDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { updateFeedbackOnSave } from 'features/AiSimulation/AiSimulationUtil';

import { saveAIRecipes } from '../../api';
import { IAIRecipeSave } from '../../api/type-def';

import SaveSimulationDetails from '../SaveSimulationDetails/SaveSimulationDetails';

import styles from './AIRecipeSave.module.scss';

const AIRecipeSave = (props: IAIRecipeSave) => {
	const {
		aiRecipeData,
		isSaveBtnVisible,
		isSaveBtnDisable,
		saveSimulationComment,
	} = props;
	const { OK, CREATED, CONFLICT } = HTTP_STATUS_CODES;

	const { SAVE } = AI_ACTIONS;
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const saveSimulationAllModal = useSelector(
		(state: RootState) => state.common.simulationCommonSaveDrawer,
	);
	const workflowFeedback = useSelector(
		(state: RootState) => state.simulationArena.workflowFeedback,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: true,
			}),
		);
	}, []);

	const saveSimulationData = async (simulationName: string) => {
		const aiData = {
			...aiRecipeData,
			name: simulationName,
		};
		const response: any = await saveAIRecipes(aiData);

		dispatch(setSimulationCommonSaveDrawer(false));
		if (response?.statusCode === OK || response?.statusCode === CREATED) {
			const responseObj = response.responseBody;
			await updateFeedbackOnSave(responseObj?.id, workflowFeedback);
			if (saveSimulationComment) {
				saveSimulationComment(
					simulationName,
					response?.responseBody?.id,
					false,
				);
			}
			dispatch(
				setToastMessage({
					message: 'The details saved successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
			navigate(urlWithTenant(`/${rrcpDashboardRoutes.simulationArena}`));
		} else if (response?.statusCode === CONFLICT) {
			dispatch(
				setToastMessage({
					message: response.errorBody,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					message: GENERIC_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const onSaveHandler = useCallback(() => {
		dispatch(setSimulationCommonSaveDrawer(true));
	}, [setSimulationCommonSaveDrawer]);

	return (
		<>
			{isSaveBtnVisible && (
				<ButtonComp
					btnType="primary"
					size="small"
					clickHandler={onSaveHandler}
					data-testid="component-save-btn"
					disabled={isSaveBtnDisable}
				>
					Save
				</ButtonComp>
			)}

			<SideDrawer
				className={styles['save-drawer']}
				drawerState={saveSimulationAllModal}
			>
				<SaveSimulationDetails
					saveSimulation={saveSimulationData}
					type={SAVE}
					name={aiRecipeData.name}
				/>
			</SideDrawer>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</>
	);
};

export default AIRecipeSave;
